import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { NewBadge } from "app/pages/sales-intelligence/common-components/badge/NewBadge";
import { IndustryTransitionEnrollButton } from "app/pages/sales-intelligence/modules/industry/components/IndustryTransitionEnrollButton";
import {
    INDUSTRY_TAXONOMY_DEMO_COPY_NAMESPACE,
    INDUSTRY_TAXONOMY_KB_ARTICLE_LINK,
} from "app/pages/sales-intelligence/modules/industry/constants";
import { useEnterIndustryTaxonomyDemo } from "app/pages/sales-intelligence/modules/industry/hooks/useEnterIndustryTaxonomyDemo";
import { useShowsIndustryTaxonomyAdminOffer } from "app/pages/sales-intelligence/modules/industry/hooks/useShowsIndustryTaxonomyAdminOffer";
import { demoModeKeys } from "app/pages/sales-intelligence/utils/i18n/demo-mode";

import { IndustryTaxonomyBannerIllustration } from "./IndustryTaxonomyBannerIllustration";
import { StyledBannerTextColumn, StyledBannerWrapper, StyledLearnMoreLink } from "./styles";

/**
 * Home-page offer inviting an admin on a legacy account to preview the industry taxonomy.
 *
 * The CTA cannot enter the demo here — entry is completed by `DemoModeBanner`, which only mounts on lead
 * generation. It instead navigates and hands the demo id over as a transition param, which the lead-gen page
 * resolves into a session once its filter panel is ready.
 */
export const IndustryTaxonomyDemoBanner = () => {
    const { translate } = useTranslate();

    const showsAdminOffer = useShowsIndustryTaxonomyAdminOffer();
    const handleTryItOut = useEnterIndustryTaxonomyDemo();

    if (!showsAdminOffer) {
        return null;
    }

    const learnMoreLabel = translate(demoModeKeys.homeBannerLearnMore(INDUSTRY_TAXONOMY_DEMO_COPY_NAMESPACE));

    return (
        <StyledBannerWrapper
            gap="24px"
            alignItems="center"
            justifyContent="space-between"
            data-automation="industry-taxonomy-demo-banner"
        >
            <StyledBannerTextColumn gap="24px">
                <Stack gap="8px">
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        <Text
                            fontVariant="h5_2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate(demoModeKeys.homeBannerTitle(INDUSTRY_TAXONOMY_DEMO_COPY_NAMESPACE))}
                        </Text>

                        <NewBadge />
                    </Flex>

                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[500]}
                        data-automation="industry-taxonomy-demo-banner-description"
                    >
                        {translate(demoModeKeys.homeBannerDescription(INDUSTRY_TAXONOMY_DEMO_COPY_NAMESPACE))}{" "}
                        <StyledLearnMoreLink
                            url={INDUSTRY_TAXONOMY_KB_ARTICLE_LINK}
                            label={learnMoreLabel}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-automation="industry-taxonomy-demo-banner-learn-more"
                        >
                            {learnMoreLabel}
                        </StyledLearnMoreLink>
                    </Text>
                </Stack>

                <Flex
                    gap="8px"
                    alignItems="center"
                    width="max-content"
                >
                    <IndustryTransitionEnrollButton type="outlined" />

                    <IconButton
                        type="primary"
                        buttonHtmlType="button"
                        width="max-content"
                        iconName="arrow-right"
                        iconSize="12px"
                        placement="right"
                        onClick={handleTryItOut}
                        data-automation="industry-taxonomy-demo-banner-try"
                    >
                        {translate(demoModeKeys.homeBannerCta(INDUSTRY_TAXONOMY_DEMO_COPY_NAMESPACE))}
                    </IconButton>
                </Flex>
            </StyledBannerTextColumn>

            <IndustryTaxonomyBannerIllustration dataAutomation="industry-taxonomy-demo-banner-illustration" />
        </StyledBannerWrapper>
    );
};
